<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import { getCurrentSeason } from '@/config/config';
import { useSeasonStore } from '@/stores/useSeasonStore';
import { normalizeForSearch } from '@/functions/utils';

interface License {
  id: number;
  license_number: number;
  previous_license_number: number | null;
  user_email: string | null;
  email: string;
  name: string;
  firstname: string;
  club_id: number;
  club_name: string;
  season: string;
  category: string;
  ligue: string;
  comite: string;
  gender: string;
  birth_date: string;
  birth_place: string | null;
  adress: string | null;
  adress_complement: string | null;
  postal_code: string | null;
  city: string | null;
  telephone: string | null;
  was_licensed_last_year: boolean | null;
  previous_club: string | null;
  previous_license_season?: string | null;
  image_rights_consent: boolean;
  fonction_dirigeante: string | null;
  declared_previous_license_number?: number | null;
  declared_previous_club?: string | null;
  declared_previous_season?: string | null;
  // Licence valable aujourd'hui (saison en cours, non quittée par un transfert).
  is_active: boolean;
}

interface UserSearchResult {
  id: number;
  name: string | null;
  firstname: string | null;
  email: string | null;
}

interface EditLicenseForm {
  // v-model sur un <input type="number"> y range un nombre dès que le champ est modifié.
  license_number: string | number;
  previous_license_number: string | number;
  season: string;
  name: string;
  firstname: string;
  gender: string;
  birth_date: string;
  birth_place: string;
  email: string;
  adress: string;
  adress_complement: string;
  postal_code: string;
  city: string;
  telephone: string;
  club_id: number | null;
  category: string;
  fonction_dirigeante: string;
  // Licence précédente : numéro, club et dernière saison jouée, rien d'autre. "Licencié
  // l'an dernier" en est déduit côté serveur (faux après une saison blanche), et
  // l'ancienne licence déclarée par le joueur n'est qu'affichée (cf. useDeclaredPrevious).
  previous_club: string;
  previous_license_season: string;
  image_rights_consent: boolean;
}

interface Comite { id: number; code: string; name: string; ligue_code: string; }
interface ClubMinimal { id: number; number: number; name: string; comite: string | null; ligue: string | null; }

const seasonStore = useSeasonStore();
const currentSeason = getCurrentSeason();

const licenses = ref<License[]>([]);
const comites = ref<Comite[]>([]);
const loading = ref(true);
const search = ref('');
const filterSeason = ref(currentSeason);
const filterLinked = ref('');
const filterLigue = ref('');
const filterComite = ref('');

const modal = ref<{ license: License; emailInput: string } | null>(null);
const saving = ref(false);
const userResults = ref<UserSearchResult[]>([]);
const userSearching = ref(false);
const showUserDropdown = ref(false);
let userSearchTimeout: ReturnType<typeof setTimeout> | null = null;

const editModal = ref<{ license: License; form: EditLicenseForm } | null>(null);
const savingEdit = ref(false);

const allClubs = ref<ClubMinimal[]>([]);
const sortedAllClubs = computed(() => [...allClubs.value].sort((a, b) => a.name.localeCompare(b.name, 'fr')));
const allClubNames = computed(() => new Set(allClubs.value.map(c => c.name)));
const previousSeasonLabel = computed(() => {
  const [start] = seasonStore.currentSeason.split('-').map(Number);
  if (!start) return '';
  return `${start - 1}-${start}`;
});
const historicalModal = ref<{ name: string; firstname: string; license_number: string; club_id: number | null; season: string } | null>(null);
const savingHistorical = ref(false);

const seasons = computed(() => {
  // Ne propose que les saisons pour lesquelles une licence existe réellement
  // (pas les saisons "en avance" créées automatiquement par seed_seasons()), mais la
  // saison courante reste toujours proposable (même vide) puisqu'elle est le filtre par défaut.
  const s = new Set(licenses.value.map(l => l.season));
  s.add(seasonStore.currentSeason);
  return Array.from(s).sort().reverse();
});

const ligues = computed(() => {
  const s = new Set(licenses.value.map(l => l.ligue).filter(Boolean));
  return Array.from(s).sort();
});

const filteredComites = computed(() => {
  if (!filterLigue.value) return comites.value;
  return comites.value.filter(c => c.ligue_code === filterLigue.value);
});

const filtered = computed(() => {
  return licenses.value.filter(l => {
    const q = normalizeForSearch(search.value);
    const matchSearch = !q || normalizeForSearch(l.email).includes(q) ||
      normalizeForSearch(l.name).includes(q) || normalizeForSearch(l.firstname).includes(q) ||
      normalizeForSearch(l.user_email).includes(q) || String(l.license_number).includes(q);
    const matchSeason = !filterSeason.value || l.season === filterSeason.value;
    const isLinked = !!l.user_email;
    const matchLinked = !filterLinked.value ||
      (filterLinked.value === 'linked' && isLinked) ||
      (filterLinked.value === 'unlinked' && !isLinked);
    const matchLigue = !filterLigue.value || l.ligue === filterLigue.value;
    const matchComite = !filterComite.value || l.comite === filterComite.value;
    return matchSearch && matchSeason && matchLinked && matchLigue && matchComite;
  });
});

const categoryLabel: Record<string, string> = {
  M: 'Masculin', F: 'Féminin', JG: 'Junior G.', JF: 'Junior F.', V: 'Vétéran', D: 'Dirigeant',
};

// Même liste que DIRIGEANT_FONCTIONS côté LSEF (LSEF/frontend/src/functions/licenses.ts) :
// valeur canonique attendue par le fichier fédéral, à garder synchronisée.
const DIRIGEANT_POSTES = ['Médecin', 'Président(e)', 'Vice-Président(e)', 'Administrateur', 'Trésorier(e)', 'Secrétaire', 'Secrétaire adjoint(e)', 'Directeur(rice) sportif(ve)', 'Directeur(rice) de la Com'];
const DIRIGEANT_FONCTIONS = [
  { label: 'Club', options: DIRIGEANT_POSTES.map(p => `${p} de Club`) },
  { label: 'Comité', options: DIRIGEANT_POSTES.map(p => `${p} de Comité`) },
  { label: 'Ligue', options: DIRIGEANT_POSTES.map(p => `${p} de Ligue`) },
  { label: 'Fédération', options: DIRIGEANT_POSTES.map(p => `${p} de la Fédération`) },
  { label: 'Autre', options: ['Autre'] },
];
const KNOWN_FONCTIONS = new Set(DIRIGEANT_FONCTIONS.flatMap(g => g.options));

const onLigueChange = () => {
  filterComite.value = '';
};

const load = async () => {
  loading.value = true;
  try {
    // all_comites=true : cette page admin doit voir toutes les ligues/comités par défaut,
    // même si le compte super_admin est aussi rattaché comme représentant d'un comité
    // (auquel cas le backend restreint sinon aux licences de ce seul comité).
    const { data } = await dartsHubApi.get('/licenses/', { params: { all_comites: true } });
    licenses.value = data;
  } catch {
    toast.error('Erreur de chargement.');
  } finally {
    loading.value = false;
  }
};

const openModal = (lic: License) => {
  modal.value = { license: lic, emailInput: lic.user_email || '' };
  userResults.value = [];
  showUserDropdown.value = false;
};

const closeModal = () => {
  modal.value = null;
  userResults.value = [];
  showUserDropdown.value = false;
};

const onEmailInputChange = () => {
  showUserDropdown.value = true;
  if (userSearchTimeout) clearTimeout(userSearchTimeout);
  const q = (modal.value?.emailInput || '').trim();
  if (q.length < 2) {
    userResults.value = [];
    return;
  }
  userSearchTimeout = setTimeout(async () => {
    userSearching.value = true;
    try {
      const { data } = await dartsHubApi.get('/users/search-admin', { params: { q } });
      userResults.value = data;
    } catch {
      userResults.value = [];
    } finally {
      userSearching.value = false;
    }
  }, 300);
};

const selectUser = (u: UserSearchResult) => {
  if (!modal.value || !u.email) return;
  modal.value.emailInput = u.email;
  userResults.value = [];
  showUserDropdown.value = false;
};

const saveLink = async () => {
  if (!modal.value) return;
  saving.value = true;
  try {
    const { data } = await dartsHubApi.patch(
      `/licenses/${modal.value.license.id}/user-link`,
      modal.value.emailInput || null,
      { headers: { 'Content-Type': 'application/json' } }
    );
    const idx = licenses.value.findIndex(l => l.id === data.id);
    if (idx !== -1) licenses.value[idx] = data;
    toast.success(data.user_email ? 'Compte lié.' : 'Compte délié.');
    closeModal();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    saving.value = false;
  }
};

const unlink = async (lic: License) => {
  saving.value = true;
  try {
    const { data } = await dartsHubApi.patch(
      `/licenses/${lic.id}/user-link`,
      null,
      { headers: { 'Content-Type': 'application/json' } }
    );
    const idx = licenses.value.findIndex(l => l.id === data.id);
    if (idx !== -1) licenses.value[idx] = data;
    toast.success('Compte délié.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    saving.value = false;
  }
};

// Saisons proposées pour la dernière saison jouée : de celle de la licence (transfert en
// cours de saison) à 1990-1991.
const previousSeasons = computed(() => {
  const season = editModal.value?.form.season || '';
  const start = Number(season.slice(0, 4)) || new Date().getFullYear();
  const seasons: string[] = [];
  for (let y = start; y >= 1990; y--) seasons.push(`${y}-${y + 1}`);
  return seasons;
});

const openEditModal = (lic: License) => {
  editModal.value = {
    license: lic,
    form: {
      license_number: String(lic.license_number),
      previous_license_number: lic.previous_license_number != null ? String(lic.previous_license_number) : '',
      season: lic.season || '',
      name: lic.name,
      firstname: lic.firstname,
      gender: lic.gender || '',
      birth_date: lic.birth_date ? lic.birth_date.slice(0, 10) : '',
      birth_place: lic.birth_place || '',
      email: lic.email || '',
      adress: lic.adress || '',
      adress_complement: lic.adress_complement || '',
      postal_code: lic.postal_code || '',
      city: lic.city || '',
      telephone: lic.telephone || '',
      club_id: lic.club_id,
      category: lic.category || '',
      fonction_dirigeante: lic.fonction_dirigeante || '',
      previous_club: lic.previous_club || '',
      previous_license_season: lic.previous_license_season || '',
      image_rights_consent: !!lic.image_rights_consent,
    },
  };
};

const closeEditModal = () => { editModal.value = null; };

// Club et dernière saison jouée d'après le numéro : licence connue la plus récente sous ce
// numéro (hors celle modifiée), sinon le club d'après la tranche du numéro (34001009 ->
// club 34001000) et la saison laissée à saisir. Toujours modifiables ensuite.
const onPreviousNumberChange = () => {
  if (!editModal.value) return;
  const form = editModal.value.form;
  const n = Number(form.previous_license_number);
  if (String(form.previous_license_number ?? '').trim() === '' || !Number.isInteger(n) || n <= 0) {
    form.previous_club = '';
    form.previous_license_season = '';
    return;
  }
  const known = licenses.value
    .filter(l => l.license_number === n && l.id !== editModal.value!.license.id && (!form.season || l.season <= form.season))
    .sort((a, b) => b.season.localeCompare(a.season))[0];
  const club = allClubs.value.find(c => c.number === Math.floor(n / 1000) * 1000);
  form.previous_club = known?.club_name || club?.name || '';
  form.previous_license_season = known?.season || '';
};

// L'ancienne licence déclarée par le joueur reste telle qu'il l'a donnée ; elle peut
// seulement servir à remplir la vraie licence précédente.
const useDeclaredPrevious = () => {
  if (!editModal.value) return;
  const lic = editModal.value.license;
  const form = editModal.value.form;
  form.previous_license_number = lic.declared_previous_license_number != null ? String(lic.declared_previous_license_number) : '';
  onPreviousNumberChange();
  if (!form.previous_club && lic.declared_previous_club) form.previous_club = lic.declared_previous_club;
  if (!form.previous_license_season && lic.declared_previous_season && lic.declared_previous_season !== 'inconnue') {
    form.previous_license_season = lic.declared_previous_season;
  }
};

const saveEdit = async () => {
  if (!editModal.value) return;
  const f = editModal.value.form;

  const licenseNumber = Number(f.license_number);
  if (!Number.isInteger(licenseNumber) || licenseNumber <= 0) {
    toast.error('Numéro de licence invalide.');
    return;
  }
  let previousNumber: number | null = null;
  if (String(f.previous_license_number ?? '').trim() !== '') {
    previousNumber = Number(f.previous_license_number);
    if (!Number.isInteger(previousNumber) || previousNumber <= 0) {
      toast.error('Numéro précédent invalide.');
      return;
    }
  }
  if (!f.name.trim() || !f.firstname.trim()) {
    toast.error('Nom et prénom requis.');
    return;
  }
  const club = allClubs.value.find(c => c.id === f.club_id);
  if (!club) {
    toast.error('Club invalide.');
    return;
  }
  // Catégorie D et fonction dirigeante vont ensemble : l'export FFD place la licence dans
  // la liste des dirigeants avec cette fonction, et le classement l'exclut.
  if (f.category === 'D' && !f.fonction_dirigeante) {
    toast.error('Fonction dirigeante requise pour une licence dirigeant.');
    return;
  }

  savingEdit.value = true;
  try {
    const { data } = await dartsHubApi.put(`/licenses/${editModal.value.license.id}`, {
      license_number: licenseNumber,
      previous_license_number: previousNumber,
      season: f.season || null,
      name: f.name,
      firstname: f.firstname,
      gender: f.gender || null,
      birth_date: f.birth_date || null,
      birth_place: f.birth_place || null,
      email: f.email || null,
      adress: f.adress || null,
      adress_complement: f.adress_complement || null,
      postal_code: f.postal_code || null,
      city: f.city || null,
      telephone: f.telephone || null,
      club_id: club.id,
      club_number: club.number,
      club_name: club.name,
      ligue: (club.ligue || '').toUpperCase(),
      comite: (club.comite || '').toUpperCase(),
      category: f.category || null,
      fonction_dirigeante: f.category === 'D' ? f.fonction_dirigeante : null,
      previous_club: previousNumber != null ? f.previous_club || null : null,
      previous_license_season: previousNumber != null ? f.previous_license_season || null : null,
      image_rights_consent: f.image_rights_consent,
    });
    const idx = licenses.value.findIndex(l => l.id === data.id);
    if (idx !== -1) licenses.value[idx] = data;
    toast.success('Licence modifiée.');
    closeEditModal();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la modification.');
  } finally {
    savingEdit.value = false;
  }
};

const openHistoricalModal = () => {
  historicalModal.value = { name: '', firstname: '', license_number: '', club_id: null, season: previousSeasonLabel.value };
};

const closeHistoricalModal = () => { historicalModal.value = null; };

const saveHistorical = async () => {
  if (!historicalModal.value) return;
  const { name, firstname, license_number, club_id, season } = historicalModal.value;
  if (!name.trim() || !firstname.trim() || !license_number || !club_id || !season.trim()) {
    toast.error('Tous les champs sont requis.');
    return;
  }
  const parsedNumber = Number(license_number);
  if (!Number.isInteger(parsedNumber) || parsedNumber <= 0) {
    toast.error('Numéro de licence invalide.');
    return;
  }
  savingHistorical.value = true;
  try {
    await dartsHubApi.post('/licenses/historical', {
      name, firstname, license_number: parsedNumber, club_id, season,
    });
    toast.success('Licence historique créée.');
    closeHistoricalModal();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la création.');
  } finally {
    savingHistorical.value = false;
  }
};

onMounted(async () => {
  await seasonStore.load();
  filterSeason.value = seasonStore.currentSeason;
  const [{ data: comitesData }, { data: clubsData }] = await Promise.all([
    dartsHubApi.get('/clubs/comites'),
    dartsHubApi.get('/clubs/all-minimal'),
  ]);
  comites.value = comitesData;
  allClubs.value = clubsData;
  await load();
});
</script>

<template>
  <div class="page">
    <div class="page-header">
      <h1><i class="fa-solid fa-id-card" /> Licences</h1>
      <button class="btn-secondary" @click="openHistoricalModal">
        <i class="fa-solid fa-clock-rotate-left" /> Licence historique
      </button>
      <div class="filters">
        <div class="search-bar">
          <i class="fa-solid fa-magnifying-glass" style="color:var(--text-muted)" />
          <input v-model="search" placeholder="Nom, email…" />
        </div>
        <select v-model="filterSeason" class="filter-select">
          <option value="">Toutes les saisons</option>
          <option v-for="s in seasons" :value="s" :key="s">{{ s }}</option>
        </select>
        <select v-model="filterLigue" class="filter-select" @change="onLigueChange">
          <option value="">Toutes les ligues</option>
          <option v-for="l in ligues" :value="l" :key="l">{{ l }}</option>
        </select>
        <select v-model="filterComite" class="filter-select">
          <option value="">Tous les comités</option>
          <option v-for="c in filteredComites" :value="c.code" :key="c.id">{{ c.name }} ({{ c.code }})</option>
        </select>
        <select v-model="filterLinked" class="filter-select">
          <option value="">Tous les comptes</option>
          <option value="linked">Compte lié</option>
          <option value="unlinked">Sans compte</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>N° licence</th>
            <th>Licencié</th>
            <th>Club</th>
            <th>Saison</th>
            <th>Catégorie</th>
            <th>Compte DartsHub</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="lic in filtered" :key="lic.id">
            <td>
              <div class="number-display">
                <span>{{ lic.license_number }}</span>
                <div v-if="lic.previous_license_number != null" class="number-sub">Préc. {{ lic.previous_license_number }}</div>
              </div>
            </td>
            <td>
              <div class="member-name">{{ lic.firstname }} {{ lic.name }}</div>
              <div class="email-cell">{{ lic.email }}</div>
            </td>
            <td>{{ lic.club_name || lic.club_id }}</td>
            <td>
              {{ lic.season || '' }}
              <span v-if="lic.is_active" class="badge badge-green" style="margin-left:4px;font-size:0.7rem;">Active</span>
              <span v-else-if="lic.season" class="badge badge-red" style="margin-left:4px;font-size:0.7rem;">Inactive</span>
            </td>
            <td>{{ categoryLabel[lic.category] || lic.category }}</td>
            <td>
              <span v-if="lic.user_email" class="badge badge-green">
                <i class="fa-solid fa-link" /> Lié
              </span>
              <span v-else class="badge badge-gray">
                <i class="fa-solid fa-link-slash" /> Non lié
              </span>
              <div v-if="lic.user_email" class="linked-email">{{ lic.user_email }}</div>
            </td>
            <td class="actions-cell">
              <button class="btn-icon" title="Modifier la licence" @click="openEditModal(lic)">
                <i class="fa-solid fa-pen" />
              </button>
              <button class="btn-icon" title="Lier un compte" @click="openModal(lic)">
                <i class="fa-solid fa-user-pen" />
              </button>
              <button v-if="lic.user_email" class="btn-icon btn-icon-danger" title="Délier" @click="unlink(lic)">
                <i class="fa-solid fa-user-xmark" />
              </button>
            </td>
          </tr>
          <tr v-if="filtered.length === 0">
            <td colspan="7" style="text-align:center;color:var(--text-muted)">Aucune licence trouvée</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="mobile-cards">
      <div v-for="lic in filtered" class="mobile-card" :key="lic.id">
        <div class="card-top">
          <div>
            <div class="member-name">{{ lic.firstname }} {{ lic.name }}</div>
            <div class="email-cell">{{ lic.email }}</div>
          </div>
          <div style="display:flex;gap:0.25rem">
            <button class="btn-icon" title="Modifier la licence" @click="openEditModal(lic)">
              <i class="fa-solid fa-pen" />
            </button>
            <button class="btn-icon" title="Lier un compte" @click="openModal(lic)">
              <i class="fa-solid fa-user-pen" />
            </button>
            <button v-if="lic.user_email" class="btn-icon btn-icon-danger" title="Délier" @click="unlink(lic)">
              <i class="fa-solid fa-user-xmark" />
            </button>
          </div>
        </div>
        <div class="number-display">
          <span>N° {{ lic.license_number }}</span>
          <div v-if="lic.previous_license_number != null" class="number-sub">Préc. {{ lic.previous_license_number }}</div>
        </div>
        <div style="font-size:0.82rem;color:var(--text-muted)">
          {{ lic.club_name || lic.club_id }} ·
          {{ categoryLabel[lic.category] || lic.category }}
        </div>
        <div style="display:flex;align-items:center;gap:0.4rem;flex-wrap:wrap">
          <span>{{ lic.season || '' }}</span>
          <span v-if="lic.is_active" class="badge badge-green" style="font-size:0.7rem">Active</span>
          <span v-else-if="lic.season" class="badge badge-red" style="font-size:0.7rem">Inactive</span>
        </div>
        <div>
          <span v-if="lic.user_email" class="badge badge-green">
            <i class="fa-solid fa-link" /> Lié
          </span>
          <span v-else class="badge badge-gray">
            <i class="fa-solid fa-link-slash" /> Non lié
          </span>
          <div v-if="lic.user_email" class="linked-email">{{ lic.user_email }}</div>
        </div>
      </div>
      <div v-if="filtered.length === 0" style="text-align:center;color:var(--text-muted);padding:1.5rem">Aucune licence trouvée</div>
    </div>

    <div v-if="!loading" class="table-count">
      {{ filtered.length }} / {{ licenses.length }} licences
    </div>

    <!-- Modal liaison -->
    <div v-if="modal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal-header">
          <span><i class="fa-solid fa-user-pen" /> Lier un compte</span>
          <button class="modal-close" @click="closeModal"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="modal-body">
          <p class="modal-desc">
            Licencié : <strong>{{ modal.license.firstname }} {{ modal.license.name }}</strong>
          </p>
          <label class="modal-label">Compte DartsHub</label>
          <div class="user-search">
            <input
              v-model="modal.emailInput"
              type="text"
              class="modal-input"
              placeholder="Nom, prénom ou email…"
              autocomplete="off"
              @input="onEmailInputChange"
              @focus="showUserDropdown = true"
              @keyup.enter="saveLink"
            />
            <div v-if="showUserDropdown && (userResults.length || userSearching)" class="user-dropdown">
              <div v-if="userSearching" class="user-option user-option-empty">
                <i class="fa-solid fa-spinner fa-spin" /> Recherche…
              </div>
              <div v-for="u in userResults" class="user-option" @mousedown.prevent="selectUser(u)" :key="u.id">
                <div class="user-option-name">{{ u.firstname }} {{ u.name }}</div>
                <div class="user-option-email">{{ u.email }}</div>
              </div>
            </div>
          </div>
          <p class="modal-hint">Recherchez par nom, prénom ou email. Laissez vide pour délier le compte actuel.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeModal">Annuler</button>
          <button class="btn-primary" :disabled="saving" @click="saveLink">
            <i v-if="saving" class="fa-solid fa-spinner fa-spin" />
            {{ modal.emailInput ? 'Lier' : 'Délier' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal édition licence -->
    <div v-if="editModal" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal modal-large">
        <div class="modal-header">
          <span><i class="fa-solid fa-pen" /> Modifier la licence</span>
          <button class="modal-close" @click="closeEditModal"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="modal-body">
          <div class="form-section-title">Licence</div>
          <div class="form-grid">
            <div class="form-field">
              <label class="modal-label">N° licence</label>
              <input v-model="editModal.form.license_number" type="number" class="modal-input" />
            </div>
            <div class="form-field">
              <label class="modal-label">Saison</label>
              <input v-model="editModal.form.season" type="text" class="modal-input" placeholder="AAAA-AAAA" />
            </div>
            <div class="form-field">
              <label class="modal-label">Catégorie</label>
              <select v-model="editModal.form.category" class="modal-input">
                <option value="">-</option>
                <option v-for="(label, code) in categoryLabel" :value="code" :key="code">{{ label }}</option>
              </select>
            </div>
            <div v-if="editModal.form.category === 'D'" class="form-field form-field-wide">
              <label class="modal-label">Fonction dirigeante</label>
              <select v-model="editModal.form.fonction_dirigeante" class="modal-input">
                <option value="" disabled>Sélectionner…</option>
                <!-- Valeur historique hors liste FFD : conservée pour ne pas l'effacer en silence. -->
                <option v-if="editModal.form.fonction_dirigeante && !KNOWN_FONCTIONS.has(editModal.form.fonction_dirigeante)"
                  :value="editModal.form.fonction_dirigeante">{{ editModal.form.fonction_dirigeante }}</option>
                <optgroup v-for="grp in DIRIGEANT_FONCTIONS" :label="grp.label" :key="grp.label">
                  <option v-for="fn in grp.options" :value="fn" :key="fn">{{ fn }}</option>
                </optgroup>
              </select>
            </div>
            <div class="form-field form-field-wide">
              <label class="modal-label">Club</label>
              <select v-model="editModal.form.club_id" class="modal-input">
                <option :value="null">Sélectionner un club…</option>
                <option v-for="c in sortedAllClubs" :value="c.id" :key="c.id">{{ c.name }} ({{ c.comite }})</option>
              </select>
            </div>
          </div>

          <div class="form-section-title">Identité</div>
          <div class="form-grid">
            <div class="form-field">
              <label class="modal-label">Nom</label>
              <input v-model="editModal.form.name" type="text" class="modal-input" />
            </div>
            <div class="form-field">
              <label class="modal-label">Prénom</label>
              <input v-model="editModal.form.firstname" type="text" class="modal-input" />
            </div>
            <div class="form-field">
              <label class="modal-label">Genre</label>
              <select v-model="editModal.form.gender" class="modal-input">
                <option value="">-</option>
                <option value="masculin">Masculin</option>
                <option value="féminin">Féminin</option>
              </select>
            </div>
            <div class="form-field">
              <label class="modal-label">Date de naissance</label>
              <input v-model="editModal.form.birth_date" type="date" class="modal-input" />
            </div>
            <div class="form-field form-field-wide">
              <label class="modal-label">Lieu de naissance</label>
              <input v-model="editModal.form.birth_place" type="text" class="modal-input" />
            </div>
          </div>

          <div class="form-section-title">Coordonnées</div>
          <div class="form-grid">
            <div class="form-field form-field-wide">
              <label class="modal-label">Email</label>
              <input v-model="editModal.form.email" type="email" class="modal-input" />
            </div>
            <div class="form-field form-field-wide">
              <label class="modal-label">Adresse</label>
              <input v-model="editModal.form.adress" type="text" class="modal-input" />
            </div>
            <div class="form-field form-field-wide">
              <label class="modal-label">Complément d'adresse</label>
              <input v-model="editModal.form.adress_complement" type="text" class="modal-input" />
            </div>
            <div class="form-field">
              <label class="modal-label">Code postal</label>
              <input v-model="editModal.form.postal_code" type="text" class="modal-input" />
            </div>
            <div class="form-field">
              <label class="modal-label">Ville</label>
              <input v-model="editModal.form.city" type="text" class="modal-input" />
            </div>
            <div class="form-field">
              <label class="modal-label">Téléphone</label>
              <input v-model="editModal.form.telephone" type="text" class="modal-input" />
            </div>
          </div>

          <div class="form-section-title">Licence précédente</div>
          <div class="form-grid">
            <div class="form-field">
              <label class="modal-label">N° précédent</label>
              <input v-model="editModal.form.previous_license_number" type="number" class="modal-input" placeholder="(aucun)" @change="onPreviousNumberChange" />
            </div>
            <div class="form-field">
              <label class="modal-label">Dernière saison jouée</label>
              <select v-model="editModal.form.previous_license_season" class="modal-input"
                :disabled="String(editModal.form.previous_license_number ?? '').trim() === ''">
                <option value="">(d'après le numéro)</option>
                <option v-for="s in previousSeasons" :value="s" :key="s">{{ s }}</option>
              </select>
            </div>
            <div class="form-field form-field-wide">
              <label class="modal-label">Club précédent</label>
              <select v-model="editModal.form.previous_club" class="modal-input"
                :disabled="String(editModal.form.previous_license_number ?? '').trim() === ''">
                <option value="">(d'après le numéro)</option>
                <!-- Ancienne saisie libre hors liste : conservée pour ne pas l'effacer en silence. -->
                <option v-if="editModal.form.previous_club && !allClubNames.has(editModal.form.previous_club)"
                  :value="editModal.form.previous_club">{{ editModal.form.previous_club }} (saisie libre)</option>
                <option v-for="c in sortedAllClubs" :value="c.name" :key="c.id">{{ c.name }} ({{ c.comite }})</option>
              </select>
            </div>
            <p class="modal-hint form-field-wide">
              Le numéro fait foi : c'est lui qui donne le type FFD (même numéro = renouvellement, autre club = transfert).
              « Licencié la saison précédente » en est déduit, faux après une saison blanche.
            </p>
            <div v-if="editModal.license.declared_previous_season || editModal.license.declared_previous_license_number != null"
              class="form-field form-field-wide declared-previous">
              <span>
                Déclaré par le joueur :
                {{ editModal.license.declared_previous_season === 'inconnue' ? 'saison inconnue' : editModal.license.declared_previous_season || 'saison ?' }}
                · n° {{ editModal.license.declared_previous_license_number ?? '?' }}
                · {{ editModal.license.declared_previous_club || 'club ?' }}
              </span>
              <button v-if="editModal.license.declared_previous_license_number != null" type="button" class="btn-link" @click="useDeclaredPrevious">
                Utiliser comme licence précédente
              </button>
            </div>
            <div class="form-field form-field-wide form-field-checkbox">
              <label class="modal-checkbox-label">
                <input v-model="editModal.form.image_rights_consent" type="checkbox" />
                Consentement droit à l'image
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeEditModal">Annuler</button>
          <button class="btn-primary" :disabled="savingEdit" @click="saveEdit">
            <i v-if="savingEdit" class="fa-solid fa-spinner fa-spin" />
            Enregistrer
          </button>
        </div>
      </div>
    </div>

    <!-- Modal licence historique -->
    <div v-if="historicalModal" class="modal-overlay" @click.self="closeHistoricalModal">
      <div class="modal">
        <div class="modal-header">
          <span><i class="fa-solid fa-clock-rotate-left" /> Licence historique</span>
          <button class="modal-close" @click="closeHistoricalModal"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="modal-body">
          <p class="modal-desc">
            Pour rattacher un licencié venant d'un club/ligue non trackée ici : seuls le nom, le club et le numéro comptent pour retrouver cette licence lors d'un renouvellement.
          </p>
          <label class="modal-label">Nom</label>
          <input v-model="historicalModal.name" type="text" class="modal-input" placeholder="ALBERTINO" />
          <label class="modal-label">Prénom</label>
          <input v-model="historicalModal.firstname" type="text" class="modal-input" placeholder="Colin" />
          <label class="modal-label">N° de licence</label>
          <input v-model="historicalModal.license_number" type="number" class="modal-input" placeholder="19001038" />
          <label class="modal-label">Club précédent</label>
          <select v-model="historicalModal.club_id" class="modal-input">
            <option :value="null">Sélectionner un club…</option>
            <option v-for="c in sortedAllClubs" :value="c.id" :key="c.id">{{ c.name }} ({{ c.comite }})</option>
          </select>
          <label class="modal-label">Saison de cette licence</label>
          <input v-model="historicalModal.season" type="text" class="modal-input" placeholder="AAAA-AAAA" />
          <p class="modal-hint">Format AAAA-AAAA. Par défaut, la saison précédente ({{ previousSeasonLabel }}).</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeHistoricalModal">Annuler</button>
          <button class="btn-primary" :disabled="savingHistorical" @click="saveHistorical">
            <i v-if="savingHistorical" class="fa-solid fa-spinner fa-spin" />
            Créer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filters {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.filter-select {
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  outline: none;
}

.email-cell,
.linked-email {
  color: var(--text-muted);
  font-size: 0.78rem;
}

.linked-email {
  margin-top: 2px;
}

.table-count {
  margin-top: 0.75rem;
  color: var(--text-muted);
  font-size: 0.8rem;
}

.member-name {
  font-weight: 500;
}

.number-display {
  display: flex;
  align-items: center;
  gap: 6px;
}

.number-sub {
  font-size: 0.72rem;
  color: var(--text-muted);
}

.badge-gray {
  background: color-mix(in srgb, var(--text-muted) 12%, transparent);
  color: var(--text-muted);
  border: 1px solid color-mix(in srgb, var(--text-muted) 20%, transparent);
}

.badge-red {
  background: color-mix(in srgb, var(--danger) 12%, transparent);
  color: var(--danger);
  border: 1px solid color-mix(in srgb, var(--danger) 20%, transparent);
}

.actions-cell {
  display: flex;
  gap: 6px;
  align-items: center;
}

.btn-icon {
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-muted);
  cursor: pointer;
  padding: 5px 8px;
  font-size: 0.8rem;
  transition: color 0.15s, border-color 0.15s;
}

.btn-icon:hover {
  color: var(--primary);
  border-color: var(--primary);
}

.btn-icon-danger:hover {
  color: var(--danger);
  border-color: var(--danger);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  width: 420px;
  max-width: 95vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.modal-large {
  width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-large .modal-body {
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  font-weight: 600;
  font-size: 0.9rem;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
}

.modal-close:hover { color: var(--text); }

.modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-desc {
  margin: 0;
  font-size: 0.88rem;
  color: var(--text-muted);
}

.modal-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.modal-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid var(--border);
  border-radius: 8px;
  background: var(--bg-input);
  color: var(--text);
  font-size: 0.87rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
}

.modal-input:focus { border-color: var(--primary); }

.modal-hint {
  margin: 0;
  font-size: 0.78rem;
  color: var(--text-muted);
}

.user-search {
  position: relative;
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  max-height: 220px;
  overflow-y: auto;
  z-index: 10;
}

.user-option {
  padding: 8px 12px;
  cursor: pointer;
}

.user-option:hover {
  background: var(--bg-input);
}

.user-option-empty {
  cursor: default;
  color: var(--text-muted);
  font-size: 0.85rem;
}

.user-option-name {
  font-size: 0.85rem;
  font-weight: 500;
}

.user-option-email {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.form-section-title {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--primary);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 6px;
  padding-top: 10px;
  border-top: 1px solid var(--border);
}

.form-section-title:first-child {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field-wide {
  grid-column: 1 / -1;
}

.declared-previous {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  font-size: 0.82rem;
  color: var(--text-muted);
}

.declared-previous .btn-link {
  background: none;
  border: none;
  padding: 0;
  color: var(--primary);
  font-size: 0.82rem;
  cursor: pointer;
  text-decoration: underline;
}

.form-field-checkbox {
  flex-direction: row;
  align-items: center;
}

.modal-checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  cursor: pointer;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid var(--border);
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 0.87rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--bg-input);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 0.87rem;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
}

.btn-secondary:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.mobile-cards { display: none; }

@media (max-width: 640px) {
  .table-wrapper { display: none; }
  .mobile-cards { display: flex; flex-direction: column; gap: 0.75rem; }
}

.mobile-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}
</style>
